import "innersvg-polyfill";
import "core-js/fn/object/assign";
import "core-js/fn/promise";
import "core-js/fn/array/find";
import "bootstrap";
import { cloneDeep } from "lodash";

// Get prepopulated values from the server where available
const resource = cloneDeep((window as any).initialReduxState.resource);
const configurationOptions = cloneDeep((window as any).configurationOptions);
const calendarDefaults = cloneDeep((window as any).calendarDefaults || {});

import * as moment from "moment";
import "moment-timezone";

// Sets the time zone and locale
moment.tz.setDefault(configurationOptions.momentTimeZone);
moment.locale(configurationOptions.momentLocale);
// Monday is the first day of the week.
moment.updateLocale(configurationOptions.momentLocale, {
    week: {
        dow: 1,
        doy: 1,
    },
});

// free memory
delete (window as any).configurationOptions;

import * as React from "react";
import * as ReactDOM from "react-dom";
import { AppContainer } from "react-hot-loader";
import { Provider } from "react-redux";
import { ConnectedRouter } from "react-router-redux";
import { createBrowserHistory } from "history";
import { ApplicationState } from "store";
import { ResourceContext } from "contexts/resource-context";
import { ConfigurationContext } from "contexts/configuration-context";
import { initializeStoreWithConfigurationOptions } from "store/configure";
import * as RoutesModule from "routes";
import "site.css";

import { ai } from "util/telemetry-service";
import {
    CustomImage,
    ImageDetails,
    PersonalDateGroup,
    PostData,
    RubberBandItem,
    SpiralItem,
} from "burde";
import { SvgCalendarLayout } from "ui/constants";

let routes = RoutesModule.routes;

// Create browser history to use in the Redux store
const baseUrl = document.getElementsByTagName("base")[0].getAttribute("href")!;
const history = createBrowserHistory({ basename: baseUrl });

// Get the application-wide store instance, prepopulating with state from the server where available.
const initialState = (window as any).initialReduxState as ApplicationState;
const postData = (window as any).postData as PostData | null;
const initialCalendarLayout = (window as any)
    .initialCalendarLayout as SvgCalendarLayout | null;
const initialFrontCoverImage = (window as any)
    .initialFrontCoverImage as ImageDetails | null;
const initialCustomCoverImage = (window as any)
    .initialCustomCoverImage as CustomImage | null;
const initialBackCoverImage = (window as any)
    .initialBackCoverImage as ImageDetails | null;
const initialCustomBackImage = (window as any)
    .initialCustomBackImage as CustomImage | null;
const initialBorderImage = (window as any)
    .initialBorderImage as ImageDetails | null;
const initialSpiral = (window as any).initialSpiral as SpiralItem | null;
const initialRubberBand = (window as any)
    .initialRubberBand as RubberBandItem | null;
const jwt = (window as any).jwt as string | null;
const initialFrontCoverSectionId = (window as any)
    .initialFrontCoverSectionId as number | null;
const initialBackCoverSectionId = (window as any).initialBackCoverSectionId as
    | number
    | null;
const showLoginModal = (window as any).showLoginModal as boolean;
const fastTrack = (window as any).fastTrack as boolean;
const initialFontId = (window as any).initialFontId as number | null;
const domain = (window as any).domain as string;

const store = initializeStoreWithConfigurationOptions(
    history,
    configurationOptions,
    calendarDefaults,
    initialState,
    postData,
    initialCalendarLayout,
    initialFrontCoverImage,
    initialCustomCoverImage,
    initialBackCoverImage,
    initialCustomBackImage,
    initialBorderImage,
    initialSpiral,
    initialRubberBand,
    jwt,
    initialFrontCoverSectionId,
    initialBackCoverSectionId,
    showLoginModal,
    fastTrack,
    initialFontId,
    domain,
);

const clientSettings = (window as any).clientSettings;

// Initialize application insights.
ai.initialize(history, clientSettings, initialState);

function renderApp() {
    // This code starts up the React app when it runs in a browser. It sets up the routing configuration
    // and injects the app into a DOM element.
    ReactDOM.hydrate(
        <AppContainer>
            <Provider store={store}>
                <ResourceContext.Provider value={resource}>
                    <ConfigurationContext.Provider value={configurationOptions}>
                        <ConnectedRouter history={history} children={routes} />
                    </ConfigurationContext.Provider>
                </ResourceContext.Provider>
            </Provider>
        </AppContainer>,
        document.getElementById("react-app"),
    );
}

renderApp();

// Allow Hot Module Replacement
if (module.hot) {
    module.hot.accept("./routes/index.tsx", () => {
        routes = require<typeof RoutesModule>("./routes/index.tsx").routes;
        renderApp();
    });
}

// Remove settings when done with initialization.
delete (window as any).clientSettings;
(window as any).clientSettings = undefined;
